import React from 'react';
import { PartnerSearchHit, PartnerStatus } from '@marageti/z4-sdk/lib/travel';
import { InfiniteScroll } from '@marageti/z4-lib';
import { Skeleton, Typography, Stack } from '@mui/material';
import PartnerCard from '../partner-card';
import useURLSearchQuery from '../../hooks/use-url-for-search-query';
import usePartnerSearch from '../../hooks/use-partner-search';
import PartnersFilters from './partners-filters';

interface DefaultSearch {
  [key: string]: string | string[];
  primaryCountries: string[];
  fullText: string;
  order: string;
  sort: string;
  status: string[];
}

const defaultSearch: DefaultSearch = {
  departureDate: '',
  primaryCountries: [],
  fullText: '',
  order: 'asc',
  sort: 'name',
  status: [
    PartnerStatus.ONBOARDING,
    PartnerStatus.NEW,
    PartnerStatus.ACTIVE,
    PartnerStatus.NON_STRATEGIC,
    PartnerStatus.PHASING_OUT,
  ],
};

const InfinitePartnersList = () => {
  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const {
    primaryCountries,
    fullText,
    status,
  } = queryValues;

  const {
    partners,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits,
  } = usePartnerSearch(queryValues);

  const onChangeFilter = (name: string, v: string | string[]) => {
    switch (name) {
      case 'primaryCountries':
        updateURLQuery({ primaryCountries: v });
        break;
      case 'fullText':
        updateURLQuery({ fullText: v });
        break;
      case 'status':
        updateURLQuery({ status: v });
        break;
      default:
        break;
    }
  };

  const formattedPartnersCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <>
      <PartnersFilters
        onChange={onChangeFilter}
        primaryCountries={primaryCountries}
        searchValue={fullText}
        selectedStatusChips={status}
      />
      {isLoading ? (
        <Skeleton variant="rounded" sx={{ mb: 2, height: '24px', width: '100px' }} />
      ) : (
        <Typography component="p" variant="h4" sx={{ mb: 1.5 }}>
          {`${formattedPartnersCount === '10,000' ? `${formattedPartnersCount}+` : formattedPartnersCount} Result${formattedPartnersCount === '1' ? '' : 's'}`}
        </Typography>
      )}
      <Stack spacing={1}>
        <InfiniteScroll
          error={error}
          hasMoreItems={hasMore}
          isLoading={isLoading}
          loadMoreItems={loadMore}
        >
          {partners?.flatMap((item: PartnerSearchHit) => (
            <PartnerCard partner={item} key={item.id} />
          ))}
        </InfiniteScroll>
      </Stack>
    </>
  );
};

export default InfinitePartnersList;
